import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Code, GraduationCap, BookOpen, Sparkles } from 'lucide-react';
import { lessonsData } from '@/data/lessonsData';
import { getLocalizedConcepts } from '@/data/localized';
import { useCourseLanguage, getLangInfo } from '@/data/languages';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { DASHBOARD } from '@/constants/testIds';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const Dashboard = () => {
  const navigate = useNavigate();
  const [lang] = useCourseLanguage();
  const langLabel = getLangInfo(lang).label;
  const conceptsData = getLocalizedConcepts(lang);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'Medium':
        return 'bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/20';
      case 'Hard':
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      default:
        return 'bg-white/10 text-white border-white/20';
    }
  };

  const groupedLessons = {
    'Array Traversal': lessonsData.filter(l => l.module === 'Array Traversal'),
    'Two Pointer': lessonsData.filter(l => l.module === 'Two Pointer'),
    'Searching': lessonsData.filter(l => l.module === 'Searching'),
    'Sorting': lessonsData.filter(l => l.module === 'Sorting'),
    'Recursion': lessonsData.filter(l => l.module === 'Recursion'),
    'Backtracking': lessonsData.filter(l => l.module === 'Backtracking'),
    'Dynamic Programming': lessonsData.filter(l => l.module === 'Dynamic Programming'),
    'Graph': lessonsData.filter(l => l.module === 'Graph'),
    'Trees': lessonsData.filter(l => l.module === 'Trees'),
    'String Algorithms': lessonsData.filter(l => l.module === 'String Algorithms')
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <Header />
      
      <main data-testid={DASHBOARD.container} className="pt-24 pb-12 max-w-7xl mx-auto px-6 flex-1">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <h1 className="font-heading text-5xl font-bold tracking-tight text-white mb-4">
            Visual Learning Modules
          </h1>
          <p className="text-[#A1A1AA] text-lg font-body">
            Pick your course, learn the basics, then master DSA through interactive visualizations
          </p>
          <div className="mt-8 flex flex-col items-center gap-2" data-testid="course-picker">
            <span className="text-[#71717A] text-xs font-code tracking-widest">CHOOSE YOUR COURSE</span>
            <LanguageSwitcher size="lg" testIdPrefix="course-lang" />
            <span className="text-[#52525B] text-xs font-body">Each course has its own progress, badges and certificates</span>
          </div>
        </motion.div>

        {/* Java Basics Section */}
        <div className="mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <span className="w-1 h-8 bg-gradient-to-b from-green-500 to-emerald-400 rounded-full" />
              <div>
                <h2 className="font-heading text-2xl font-semibold text-white flex items-center gap-2">
                  <GraduationCap className="w-6 h-6 text-green-400" />
                  <span data-testid="basics-section-title">{langLabel} Basics</span>
                </h2>
                <p className="text-[#71717A] text-sm font-body">
                  {conceptsData.length} beginner-friendly lessons with quizzes
                </p>
              </div>
            </div>
            <Badge className="bg-green-500/10 text-green-400 border-green-500/20 border font-code text-xs">
              START HERE
            </Badge>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {conceptsData.map((concept, index) => (
              <motion.div
                key={concept.id}
                data-testid={`concept-card-${concept.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.04 }}
                whileHover={{ y: -3 }}
                className="bg-[#0F0F12] border border-white/10 rounded-xl p-5 transition-all duration-300 hover:border-green-500/50 hover:shadow-[0_8px_30px_rgba(34,197,94,0.1)] cursor-pointer group"
                onClick={() => navigate(`/concept/${concept.id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-400 font-code font-bold text-sm">
                    {concept.order}
                  </div>
                  <div className="flex items-center gap-1 text-[#71717A] text-xs font-code">
                    <Clock className="w-3 h-3" />
                    {concept.estimatedTime}
                  </div>
                </div>

                <h3 className="font-heading text-base font-semibold text-white mb-1 group-hover:text-green-400 transition-colors">
                  {concept.title}
                </h3>
                <p className="text-[#A1A1AA] text-xs font-body leading-relaxed mb-4">
                  {concept.subtitle}
                </p>

                <div className="flex items-center justify-between text-xs font-code">
                  <div className="flex items-center gap-3 text-[#71717A]">
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-3 h-3" />
                      Learn
                    </span>
                    <span className="flex items-center gap-1">
                      <Code className="w-3 h-3" />
                      {concept.codeExamples.length}
                    </span>
                    <span>Quiz · {concept.quiz.length}Q</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-green-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="mb-12 flex items-center gap-4">
          <div className="h-px flex-1 bg-white/10" />
          <div className="text-[#71717A] text-sm font-code">Then dive into DSA problems</div>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        {/* Spinning Wheel Challenge CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          data-testid="challenges-cta"
          onClick={() => navigate('/challenges')}
          whileHover={{ y: -3 }}
          className="mb-14 cursor-pointer relative overflow-hidden rounded-2xl border border-[#FFB800]/30 bg-gradient-to-br from-[#FFB800]/10 via-[#EC4899]/5 to-[#050505] p-8 group hover:border-[#FFB800]/60 transition-all duration-300 hover:shadow-[0_10px_60px_rgba(255,184,0,0.15)]"
        >
          <div
            className="absolute -right-10 -top-10 w-64 h-64 rounded-full opacity-20 blur-3xl group-hover:opacity-30 transition-opacity"
            style={{ background: 'radial-gradient(circle, #FFB800, transparent)' }}
          />
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FFB800] to-[#FF8800] flex items-center justify-center flex-shrink-0 shadow-[0_0_30px_rgba(255,184,0,0.4)]">
                <Sparkles className="w-8 h-8 text-black" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-[#EC4899]/10 text-[#EC4899] border-[#EC4899]/30 border font-code text-[10px]">
                    NEW
                  </Badge>
                  <span className="text-[#71717A] font-code text-xs">GAMIFIED</span>
                </div>
                <h3 className="font-heading text-2xl font-bold text-white mb-1">
                  Spin the DSA Challenge Wheel
                </h3>
                <p className="text-[#A1A1AA] font-body text-sm">
                  Random topic. 5 MCQs. Instant feedback. Kapil tracks it live.
                </p>
              </div>
            </div>
            <Button
              data-testid="challenges-cta-button"
              className="bg-white text-black hover:bg-white/90 font-heading font-bold px-6 py-5 group-hover:scale-105 transition-transform"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/challenges');
              }}
            >
              Spin Now
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          data-testid="assessment-cta"
          onClick={() => navigate('/assessment')}
          whileHover={{ y: -3 }}
          className="mb-14 -mt-8 cursor-pointer rounded-2xl border border-[#007AFF]/30 bg-gradient-to-br from-[#007AFF]/10 to-[#050505] p-8 hover:border-[#007AFF]/60 transition-colors duration-300"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#007AFF] to-[#0055CC] flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
              <div>
                <span className="text-[#71717A] font-code text-xs">PLACEMENT READINESS</span>
                <h3 className="font-heading text-2xl font-bold text-white mb-1">Final Assessment</h3>
                <p className="text-[#A1A1AA] font-body text-sm">
                  250 industry-oriented MCQs · 60 minutes · downloadable PNG & PDF scorecard.
                </p>
              </div>
            </div>
            <Button
              data-testid="assessment-cta-button"
              className="bg-[#007AFF] text-white hover:bg-[#1A8AFF] font-heading font-bold px-6 py-5"
              onClick={(e) => { e.stopPropagation(); navigate('/assessment'); }}
            >
              Take Assessment
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </motion.div>

        {Object.entries(groupedLessons).map(([module, lessons], moduleIndex) => (
          <div key={module} className="mb-12">
            <motion.h2
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: moduleIndex * 0.1 }}
              className="font-heading text-2xl font-semibold text-white mb-6 flex items-center gap-3"
            >
              <span className="w-1 h-8 bg-gradient-to-b from-[#FFB800] to-[#FF8800] rounded-full" />
              {module}
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {lessons.map((lesson, index) => (
                <motion.div
                  key={lesson.id}
                  data-testid={DASHBOARD.lessonCard(lesson.id)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: moduleIndex * 0.1 + index * 0.1 
                  }}
                  whileHover={{ y: -4 }}
                  className="bg-[#0F0F12] border border-white/10 rounded-xl p-6 transition-all duration-300 hover:border-[#FFB800]/50 hover:shadow-[0_8px_30px_rgba(255,184,0,0.1)] cursor-pointer group"
                  onClick={() => navigate(`/lesson/${lesson.id}`)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <Badge 
                      className={`${getDifficultyColor(lesson.difficulty)} border font-code text-xs px-2 py-1`}
                    >
                      {lesson.difficulty}
                    </Badge>
                    <div className="flex items-center gap-1 text-[#71717A] text-xs font-code">
                      <Clock className="w-3 h-3" />
                      {lesson.estimatedTime}
                    </div>
                  </div>

                  <h3 className="font-heading text-xl font-semibold text-white mb-2 group-hover:text-[#FFB800] transition-colors">
                    {lesson.title}
                  </h3>

                  <div className="flex items-start gap-2 mb-6">
                    <Code className="w-4 h-4 text-[#007AFF] mt-0.5 flex-shrink-0" />
                    <p className="text-[#A1A1AA] text-sm font-body leading-relaxed">
                      {lesson.concept}
                    </p>
                  </div>

                  <Button
                    data-testid={DASHBOARD.startButton(lesson.id)}
                    className="w-full bg-gradient-to-r from-[#FFB800]/10 to-[#FF8800]/10 hover:from-[#FFB800]/20 hover:to-[#FF8800]/20 text-[#FFB800] border border-[#FFB800]/30 font-heading font-medium transition-all duration-300 group-hover:shadow-[0_0_20px_rgba(255,184,0,0.2)]"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/lesson/${lesson.id}`);
                    }}
                  >
                    Start Visualization
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </main>

      <Footer />
    </div>
  );
};

export default Dashboard;
